import {
  Banknote,
  Calendar,
  TrendingDown,
  Wallet,
} from "lucide-react";
import type { VoucherEmployee } from "@/features/vouchers/types/voucherEmployee.type";
import { EuroFormat } from "@/shared/components/euroFormat";
import { formatDateToLong } from "@/shared/utils/format.util";

type VoucherSummaryCardProps = { voucher: VoucherEmployee };

type SummaryItemProps = {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
  variant?: "amount" | "balance" | "date"
};

const VALUE_CLASSES: Record<NonNullable<SummaryItemProps["variant"]>, string> = {
  amount: "text-lg font-semibold text-slate-900 dark:text-white",
  balance: "text-lg font-bold text-primary",
  date: "text-sm font-semibold text-slate-900 dark:text-white",
};

const SummaryItem = ({
  icon,
  label,
  children,
  variant = "amount",
}: SummaryItemProps) => {
  const valueClass = VALUE_CLASSES[variant];

  return (
    <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 lg:flex-col lg:items-start lg:gap-1">
      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-slate-200/50 dark:bg-slate-700/50 flex-shrink-0 lg:h-7 lg:w-7">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
        <div className={valueClass}>{children}</div>
      </div>
    </div>
  );
};

export const VoucherSummaryCard = ({ voucher }: VoucherSummaryCardProps) => {
  const spent = voucher.amount - voucher.balance;
  const isCredit = spent < 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
      <SummaryItem
        icon={<Banknote size={18} className="text-slate-500 dark:text-slate-400" />}
        label="Montant initial"
      >
        <EuroFormat amount={voucher.amount} />
      </SummaryItem>

      <SummaryItem
        icon={<TrendingDown size={18} className="text-slate-500 dark:text-slate-400" />}
        label={isCredit ? "Remboursements" : "Dépensé"}
      >
        <EuroFormat
          amount={Math.abs(spent)}
          className={isCredit ? "text-green-600 dark:text-green-400" : undefined}
        />
      </SummaryItem>

      <SummaryItem
        icon={<Wallet size={18} className="text-primary" />}
        label="Solde disponible"
        variant="balance"
      >
        <EuroFormat amount={voucher.balance} />
      </SummaryItem>

      <SummaryItem
        icon={<Calendar size={18} className="text-slate-500 dark:text-slate-400" />}
        label="Date d'échéance"
        variant="date"
      >
        {formatDateToLong(voucher.end_date)}
      </SummaryItem>
    </div>
  );
};

VoucherSummaryCard.displayName = "VoucherSummaryCard";
